import { Divider } from "@mui/material";
import { centroid } from "@turf/turf";
import { LngLat, Map } from "mapbox-gl";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { client } from "../..";
import { getContractFromBi } from "../../api/contract";
import { EmptyView } from "../../components/EmptyView";
import { FirebaseImage } from "../../components/FirebaseImage";
import { Loader } from "../../components/Loader";
import MapBox from "../../components/MapBox";
import { convertToDateJakartaTime, DATE_PATTERN_DD_MMM_YYYY_NODASH, defaultLat, defaultLng, isArrayNullOrEmpty, showErrorMessage } from "../../helper/helper";
import { addContractDetailLayers, updatePickedUps, updatePlots, zoomForCoordinates } from "../../models/MapBoxDataModel";
import { ContractBI } from "../../models/contract/ContractBI";
import { ContractInterface } from "../../models/contract/ContractInterface";
import { ContractPhoto } from "../../models/contract/ContractPhoto";
import { Harvest } from "../../models/contract/Harvest";
import { LineItem } from "../../models/contract/LineItem";
import { Organisation } from "../../models/organisation/OrganisationModelV2";
import { ReactComponent as Check } from "../../rsc/check.svg";
import { colorDark, colorPrimary } from "../../rsc/colors/colors";
import { ReactComponent as LogoDO } from "../../rsc/ic_do.svg";
import { ReactComponent as Money } from "../../rsc/ic_money.svg";
import { ReactComponent as Supply } from "../../rsc/ic_supply.svg";
import "../contracts/ContractDetail.css";

export const ContractTraceableDetail: React.FC = () => {

    const [contract, setContract] = useState<ContractBI>(undefined)
    const [center, setCenter] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const mapRef = useRef(null)
    const contractRef = useRef<ContractBI>(null)
    const popupsRef = useRef([])
    const pickedUpLocation = useRef([])
    const [pickUpOnMap, setPickUpOnMap] = useState([])
    const [supplier, setSupplier] = useState<Organisation | undefined>(undefined)

    const { id } = useParams()
    // var contractId = param.get("contract_id")

    const { t, i18n } = useTranslation()

    function fetchContractFromSale(saleId: number, type) {
        setIsLoading(true)
        const query = getContractFromBi
        client.query({
            query: query,
            variables: {
                "filter": {
                    "contract": { "id": parseInt(id) }
                }
            },
            context: {
                headers: {
                    "role": "usermill"
                }
            }
        }).then((data) => {
            const contract = data.data.contract?.at(0)
            if (contract) {
                const contractBuilt = ContractBI.build(contract)
                contractRef.current = contractBuilt
                setContract(contractBuilt)
            }
            // var contractBuilt: ContractInterface
            // ContractBI.
            // setCenter(extractCenter(contractBuilt))
            // setContract(contractBuilt)
            // contractRef.current = contractBuilt
        }).catch(e => {
            showErrorMessage(e.message)
        })
            .finally(() => {
                setIsLoading(false)
            })
    }

    // function fetchSaleFromContractId(cId: number) {
    //     setIsLoading(true)
    //     const query = GetSaleFromContract
    //     props.client.query({
    //         query: query,
    //         variables: {
    //             id: cId
    //         },
    //     }).then((data) => {
    //         console.log(data)
    //         // fetchContractFromSale(data.data)
    //     }).finally(() => {
    //         setIsLoading(false)
    //     })
    // }

    function centerMap() {
        var coords: LngLat[] = []
        if (contractRef.current?.getHarvest()?.length > 0) {
            coords = [...contractRef.current.getHarvest()]?.filter(h => h.land_plot).map(h => h.land_plot).flatMap(p => p.polygon ? p.polygon.coordinates[0].map(co => new LngLat(co[0], co[1])) : [])
        }
        if (pickedUpLocation.current?.length > 0) {
            coords = [...coords, ...pickedUpLocation.current.map(co => new LngLat(co[0], co[1]))]
        }
        zoomForCoordinates(mapRef.current, coords, 14, 0)
    }

    useEffect(() => {
        if (id) {
            fetchContractFromSale(Number(id), undefined)
        }
    }, [id])

    useEffect(() => {
        if (contract) {
            setSupplier(contract?.getFarm() ?? contract?.getAgent())
            addPickedUpLocations(contract.harvestPhotos().map(p => p.getCoordinates()))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contract])

    useEffect(() => {
        if (contractRef.current) {
            updatePlots(mapRef.current, contractRef.current.getHarvest())
            centerMap()
        }
    }, [contractRef.current])

    useEffect(() => {
        const changeLanguageHandler = (l) => {
            if (mapRef.current) {
                // if (contract.isAgentContract()) {
                //     addContractDetailNAMLayers(mapRef.current)
                // } else {
                //     addContractDetailNAFLayers(mapRef.current, contractRef.current.harvests.map(h => h.harvest), popupsRef.current, t)
                // }
            }
        };

        i18n.on('languageChanged', changeLanguageHandler);

        return () => {
            i18n.off('languageChanged', changeLanguageHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n])

    const extractCenter = (contractExtract: ContractInterface) => {
        const harvest = contractExtract.getHarvest()?.at(0)
        if (harvest) {
            let coo = harvest?.land_plot?.polygon?.coordinates
            if (coo) {
                let middle = centroid(
                    {
                        'type': 'Feature',
                        'properties': {
                        },
                        'geometry': {
                            'type': 'Polygon',
                            'coordinates': coo
                        }
                    }
                )
                return middle.geometry.coordinates
            } else {
                return null
            }
        }
    }

    const marketDay = () => {
        if (contract?.getMargetDay()) {
            return convertToDateJakartaTime(contract?.getMargetDay(), DATE_PATTERN_DD_MMM_YYYY_NODASH)
        }
    }

    function addPickedUpLocations(coordinates: number[][]) {
        const newCoordinates = coordinates.filter(c => !isArrayNullOrEmpty(c))
        if (isArrayNullOrEmpty(newCoordinates)) {
            return
        }
        pickedUpLocation.current = [...pickedUpLocation.current, ...newCoordinates]
        setPickUpOnMap(pickedUpLocation.current)
        updatePickedUps(mapRef.current, pickedUpLocation.current)
        centerMap()
    }

    function fruitPhotosSection(title: string, photos: ContractPhoto[], sectionKey: string) {
        // keep one photo per uri, the same picture can be attached to the harvest and to the sale
        const uniquePhotos = (photos ?? []).filter((p, i, all) => all.findIndex(o => o.uri === p.uri) === i)
        if (isArrayNullOrEmpty(uniquePhotos)) {
            return <div key={sectionKey}></div>
        }
        return (
            <div className="cd-photo-section" key={sectionKey}>
                <div className="cd-photo-section-header">
                    <Supply />
                    <p>{title}</p>
                </div>
                <div className="cd-photo-grid">
                    {uniquePhotos.map(p =>
                        <FirebaseImage
                            key={p.uri}
                            style={{ width: "100%", height: "100%" }}
                            uri={p.uri}
                            // the gps of the photo is already given by the api on some contracts,
                            // otherwise it has to be read from the file metadata
                            onMetadataLoaded={p.getCoordinates() ? undefined : (metadata) => {
                                try {
                                    const jsonObject = JSON.parse(metadata?.photo_location);
                                    addPickedUpLocations([jsonObject.coordinates])
                                } catch (error) {
                                    console.error('Error parsing JSON:', error);
                                }
                            }} />
                    )}
                </div>
            </div>
        )
    }

    function harvestsPhotos(harvest: Harvest) {
        return fruitPhotosSection(
            t('contract_land_plot_header', { name: harvest.land_plot?.name }),
            harvest.fruitPhotos(),
            `harvest-${harvest.land_plot?.name}`)
    }

    function displayReceipt(contract: ContractBI) {
        const photos = contract.receiptPhotos().map(p => p.uri)
        return (
            photos ?
                <div className="cd-photo-section">
                    <div className="cd-photo-section-header">
                        <Money />
                        <p>{t('contract_receipt_header')}</p>
                    </div>
                    <div className="cd-photo-grid">
                        {
                            photos.map(p =>
                                <FirebaseImage key={p} style={{ width: "100%", height: "100%" }} uri={p} />
                            )
                        }
                    </div>
                </div>
                : <></>
        )
    }

    function shouldDisplayHarvest() {
        return (contract?.harvestPhotos()?.length ?? 0) > 0
    }

    function shouldDisplayReceipt(): boolean {
        return !isArrayNullOrEmpty(contract?.receiptPhotos())
    }

    function generateHarvestPicture() {
        const harvests = contract?.getHarvest()
        if (!isArrayNullOrEmpty(harvests)) {
            return harvests.map(h => harvestsPhotos(h))
        }
        // no harvest attached to the contract, the fruit photos are carried by the contract itself
        return fruitPhotosSection(t('contract_fruit_header'), contract?.harvestPhotos(), "contract-fruit-photos")
    }

    function displayWeightInfo() {
        const lineItems = contract?.getBuyerLineItem()?.map((li: LineItem, index) => {
            const displayTitle = li.isETaxLineItem() ? "none" : "block"
            return (
                <div key={index} style={{ display: "flex", color: colorDark() }}>
                    <div style={{ flex: "2", display: "flex" }}>
                        <div style={{ flex: 1, display: displayTitle }}>
                            <div style={{ fontSize: "12px" }}>{li.title ?? li.sku}</div>
                            <div style={{ textAlign: "start", display: "flex", flex: 1 }}>
                                <span style={{ marginRight: "5px", fontWeight: "700", fontSize: "18px" }}>{li.price_per_unit}</span>
                                <span style={{ marginRight: "10px", fontWeight: "700", fontSize: "11px" }}>Rp/KG</span>
                                <span style={{ marginRight: "10px", fontWeight: "700", fontSize: "11px" }}>x</span>
                                <span style={{ marginRight: "5px", fontWeight: "700", fontSize: "18px" }}>{li.quantity}</span>
                                <span style={{ marginRight: "10px", fontWeight: "700", fontSize: "11px" }}>KG</span>
                            </div>
                        </div>
                        <span style={{ marginRight: "10px", fontWeight: "700", fontSize: "11px", alignContent: "end", display: displayTitle }}>=</span>
                    </div>
                    <div style={{ flex: "1", textAlign: "end", display: "flex" }}>
                        <span style={{ marginRight: "5px", fontWeight: "700", color: li.getTotal() > 0 ? colorDark() : "red", fontSize: "18px", flex: 1, alignSelf: "end" }}>{li.getTotal()?.IndoFormat()}</span>
                        <span style={{ marginRight: "0px", fontWeight: "700", color: li.getTotal() > 0 ? colorDark() : "red", fontSize: "11px", alignSelf: "end" }}>Rp</span>
                    </div>
                </div>
            )
        })

        return <div>
            <div style={{ display: "flex", gap: "10px", flexDirection: "column", marginBottom: "8px" }}>
                {lineItems}
            </div>
            <Divider />
            {contract?.getBuyerSaleStatement() &&
                <div>
                    {/* {
                        contract?.getSaleStatement()?.getPreTaxTotal() &&
                        <div style={{ marginTop: "20px", display: "flex" }}>
                            <span style={{ marginRight: "5px", fontWeight: "700", color: "black", fontSize: "18px", flex: 1 }}>{t('total')}</span>
                            <span style={{ marginRight: "5px", fontWeight: "700", color: "black", fontSize: "20px" }}>{contract.getSaleStatement()?.getPreTaxTotal()?.IndoFormat()}</span>
                            <span style={{ marginRight: "0px", fontWeight: "700", color: "black", fontSize: "11px" }}>Rp</span>
                        </div>
                    }
                    {
                        contract?.getSaleStatement()?.getTaxTotal() &&
                        <div style={{ marginTop: "20px", display: "flex" }}>
                            <span style={{ marginRight: "5px", fontWeight: "700", color: "black", fontSize: "18px", flex: 1 }}>{t('tax_total')}</span>
                            <span style={{ marginRight: "5px", fontWeight: "700", color: "black", fontSize: "20px" }}>{contract.getSaleStatement()?.getTaxTotal()?.IndoFormat()}</span>
                            <span style={{ marginRight: "0px", fontWeight: "700", color: "black", fontSize: "11px" }}>Rp</span>
                        </div>
                    } */}
                    < div style={{ marginTop: "20px", display: "flex" }}>
                        <span style={{ marginRight: "5px", fontWeight: "700", fontSize: "26px", flex: 1 }}>{t('total')}</span>
                        <span style={{ marginRight: "5px", fontWeight: "700", fontSize: "26px" }}>{contract.getBuyerSaleStatement()?.total?.IndoFormat()}</span>
                        <span style={{ marginRight: "0px", fontWeight: "700", fontSize: "11px" }}>Rp</span>
                    </div>
                </div>
            }
        </div >
    }

    function hasPositionOnMap(): boolean {
        return contract?.getHarvest()?.length > 0 || pickUpOnMap.length > 0
    }

    function onMapLoaded(map: Map) {
        mapRef.current = map
        addContractDetailLayers(mapRef.current, popupsRef.current)
        if (!isArrayNullOrEmpty(pickedUpLocation.current)) {
            updatePickedUps(mapRef.current, pickedUpLocation.current)
        }
        if (contractRef.current) {
            updatePlots(mapRef.current, contractRef.current.getHarvest())
            centerMap()
        }
    }

    function webGlNotActivated() {
        // map not available, nothing to do
    }

    return (
        <div style={{ flex: "1", paddingBottom: "20px" }}>
            <div style={{ padding: "20px 28px 0 28px" }}>
                <h1 style={{ margin: "0 0 20px 0" }}>{t('contract')} {id}</h1>

                {!isLoading && !contract ?
                    <EmptyView type="check" isVisible={true} title={t('no_contract_found')} />
                    :
                    contract && <>
                        {/* Buyer bar with status */}
                        <div className="cd-buyer-bar">
                            <div className="cd-buyer-info">
                                <LogoDO />
                                <p className="cd-buyer-name">{contract?.getBuyer()?.name}</p>
                                <p className="cd-market-day">{marketDay()}</p>
                            </div>
                            <div className="cd-status-badge">
                                <Check fill={colorPrimary()} />
                                <span>{contract?.getStatus()}</span>
                            </div>
                        </div>

                        {/* Supplier + Weight info */}
                        <div className="cd-middle-section">
                            <div className="cd-middle-left">
                                <p className="cd-section-label">{contract?.getType() === "NAF_V3" ? t('transporter') : contract?.getFarm() ? t('farm') : t('agent')}</p>
                                <div className="cd-supplier-card">
                                    {supplier ?
                                        <>
                                            <Supply style={{ width: "36px", height: "36px", flexShrink: 0 }} />
                                            <div className="cd-supplier-info">
                                                <p className="cd-supplier-name">{supplier.name ?? "—"}</p>
                                                {supplier.location?.village &&
                                                    <p className="cd-supplier-location">{supplier.location.village}</p>
                                                }
                                            </div>
                                        </>
                                        :
                                        <div className="cd-supplier-info">
                                            <p className="cd-supplier-name cd-supplier-empty">— {t('supplier_not_available')}</p>
                                        </div>
                                    }
                                </div>
                            </div>
                            <div className="cd-middle-right">
                                {displayWeightInfo()}
                            </div>
                        </div>

                        {/* Map + Photos */}
                        <div className="map-photo-container">
                            {
                                shouldDisplayHarvest() || shouldDisplayReceipt() ?
                                    <div className="photo-container">
                                        {shouldDisplayHarvest() ? generateHarvestPicture() : <></>}
                                        {shouldDisplayReceipt() ? displayReceipt(contract) : <></>}
                                    </div>
                                    : <></>
                            }
                            <div className="map-container-contract">
                                <div className="map-container-contract-ar">
                                    <div style={{ position: "relative", width: "100%", height: "100%" }}>
                                        <MapBox lat={center ? center[1] : defaultLat} lng={center ? center[0] : defaultLng} onLoaded={(map) => { onMapLoaded(map) }} onWebGlDisable={webGlNotActivated} />
                                        {
                                            !hasPositionOnMap() &&
                                            <div className="cd-map-empty-overlay">
                                                <EmptyView type="check" isVisible={true} title={t('no_harvest_for_contract')} />
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }
            </div>
            <Loader isLoading={isLoading} />
        </div>
    )
}